import { useRef, FocusEvent, SyntheticEvent, useEffect } from 'react';
import { BaseAutocomplete } from '../../../BaseAutocomplete/BaseAutocomplete';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../../context/Keyboard/keyboard.types';
import { useField, useFormikContext } from 'formik';
import { useKeyboardIntegration } from '../../../../../context/Keyboard/keyboard';
import { KioskFormAutocompleteProps } from '../FormAutocomplete.types';
import { FilterOptionsState } from '@mui/material';
import { useScrollToOnError } from '../../../../../helpers/useScrollTo';

export const KioskFormAutocomplete = <T,>({
    name,
    id,
    title,
    label,
    ariaLabel,
    labelClass,
    disabled,
    options,
    forceBottomPopper,
    clearOnBlur,
    keyboardLayout,
    shiftAfterPeriod,
    shiftAfterSpace,
    shiftAfterAmpersand,
    hideKeyboardOnClickOut,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
}: KioskFormAutocompleteProps<T>) => {
    const [field, meta] = useField(name);
    const { submitCount, setFieldValue } = useFormikContext();

    const inputRef = useRef<HTMLInputElement>(null);
    const isFocused = useRef<boolean>(false);
    const valueRef = useRef<string>(field.value ?? '');
    const caretPositionRef = useRef<number | null>(null);
    const wasOSKInput = useRef<boolean>(false);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const updateFieldValue = (option: string | T | null) => {
        return setFieldValue(name, (option as string)?.toUpperCase());
    };

    const getOptionLabel = (option: string | T): string => {
        return option as string;
    };

    const renderOption = (option: T): string => {
        return option as string;
    };

    const filterOptions = (options: T[], state: FilterOptionsState<T>): T[] => {
        if (state.inputValue === '') {
            return options;
        }
        const filteredOptions = options.filter((option: T) => (option as string)?.toLocaleLowerCase()
            .includes(state.inputValue?.toLocaleLowerCase()));
        if (filteredOptions.length > 0) {
            return filteredOptions;
        }
        else {
            return [];
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
        }

        if (wasOSKInput.current) {
            wasOSKInput.current = false;
        }
    }, [inputRef.current?.value]);

    useEffect(() => {
        if (valueRef.current !== field.value) {
            valueRef.current = field.value;
        }
    }, [field.value]);

    const getValue = (): GetValueReturn => {
        wasOSKInput.current = true;

        if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
            const selectionStart: number = inputRef.current.selectionStart;
            const selectionEnd: number = inputRef.current.selectionEnd;

            return {
                inputValue: valueRef.current,
                caretPositionStart: selectionStart,
                caretPositionEnd: selectionEnd,
            };
        }
        else {
            return {
                inputValue: valueRef.current,
                caretPositionStart: valueRef.current.length,
                caretPositionEnd: valueRef.current.length,
            };
        }
    };

    const setValue = ({ value, caretPosition }: SetValueProps): void => {
        caretPositionRef.current = caretPosition;
        valueRef.current = value;
        updateFieldValue(value);
    };

    const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
        return {
            value: value,
            caretPosition: caretPosition,
        };
    };

    const onKeyboardInput = (): void => {
        if (inputRef.current && !isFocused.current) {
            inputRef.current.focus();
        }
    };

    const { onFocus, onBlur } = useKeyboardIntegration({
        fieldValue: field.value,
        getValue: getValue,
        setValue: setValue,
        onInputChange: onInputChange,
        onKeyboardInput: onKeyboardInput,
        keyboardLayout: keyboardLayout,
        shiftAfterPeriod: shiftAfterPeriod,
        shiftAfterSpace: shiftAfterSpace,
        shiftAfterAmpersand: shiftAfterAmpersand,
        hideKeyboardOnClickOut: hideKeyboardOnClickOut,
        capsLock: capsLock,
        clearOnBlur: clearOnBlur,
    });

    const handleOnBlur = () => {
        onBlur();
        isFocused.current = false;
    };

    const handleOnChange = (event: SyntheticEvent<Element, Event>, value: string) => {
        if (!wasOSKInput.current) {
            caretPositionRef.current = (event.target as HTMLInputElement).selectionEnd;
        }
        valueRef.current = value;
        updateFieldValue(value);
    };

    const handleOnFocus = (event: FocusEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        isFocused.current = true;
        onFocus(event);
    };

    return (
        <BaseAutocomplete
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            labelClass={labelClass}
            inputRef={inputRef}
            inputValue={field.value ?? ''}
            disabled={disabled}
            touched={meta.touched || submitCount > 0}
            error={meta.error}
            options={options}
            filterOptions={filterOptions}
            onOptionSelected={updateFieldValue}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            onInputChange={handleOnChange}
            forceBottomPopper={forceBottomPopper}
            capsLock={capsLock}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            freeSolo={true}
            openOnFocus={true}
        />
    );
};